import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { Button, IconButton, Stack } from "@mui/material";
import React, { useState, useEffect } from "react";

export default function CustomPagination({
  totalCount = 0,
  showPages = 0,
  pagesArray = 0,
  pageSize = 10,
  currentPage = 1,
  getCurrentPageData,
  setTotalPages,
  show = true,
}) {
  const [totalPages, setInternalTotalPages] = useState(0);
  const [displayPages, setDisplayPages] = useState([]);

  useEffect(() => {
    // Calculate total pages
    const calculatedTotalPages = Math.ceil(totalCount / pageSize);
    setInternalTotalPages(calculatedTotalPages);

    // Update parent's total pages if callback provided
    if (setTotalPages) {
      setTotalPages(calculatedTotalPages);
    }

    // Calculate pages to display
    const visiblePages = showPages || (calculatedTotalPages > 200 ? 7 : 5);
    const pages = getPageNumbers(
      currentPage,
      calculatedTotalPages,
      visiblePages,
    );
    setDisplayPages(pages);
  }, [totalCount, pageSize, currentPage, showPages, setTotalPages]);

  // Generate array of page numbers to show
  const getPageNumbers = (current, total, visible) => {
    let pages = [];

    if (total <= visible) {
      // Show all pages if total is less than visible count
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      // Calculate range around current page
      let startPage = Math.max(1, current - Math.floor(visible / 2));
      let endPage = Math.min(total, startPage + visible - 1);

      // Adjust start if we're near the end
      if (endPage - startPage < visible - 1) {
        startPage = Math.max(1, endPage - visible + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && getCurrentPageData) {
      getCurrentPageData(newPage, pageSize);
    }
  };

  if (!show || totalCount === 0) return null;

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <IconButton
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        size="small"
        sx={{
          color: "text.secondary",
          "&.Mui-disabled": {
            color: "grey.300",
          },
        }}
      >
        <KeyboardArrowLeft />
      </IconButton>

      {displayPages.map((pageNum) => (
        <Button
          key={pageNum}
          onClick={() => handlePageChange(pageNum)}
          variant={pageNum === currentPage ? "contained" : "text"}
          size="small"
          sx={{
            minWidth: "32px",
            height: "32px",
            p: 0,
          }}
        >
          {pageNum}
        </Button>
      ))}

      <IconButton
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        size="small"
        sx={{
          color: "text.secondary",
          "&.Mui-disabled": {
            color: "grey.300",
          },
        }}
      >
        <KeyboardArrowRight />
      </IconButton>
    </Stack>
  );
}
